import { Button } from "@/components/ui/button";
import { ArrowRight, Heart } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { StampCard } from "@/components/StampCard";

function FloatingShapes() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        animate={{ y: [0, -30, 0], x: [0, 15, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-primary/[0.07] blur-[100px]"
      />
      <motion.div
        animate={{ y: [0, 20, 0], x: [0, -10, 0], scale: [1, 1.08, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full bg-accent/[0.06] blur-[80px]"
      />
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [45, 55, 45] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute top-1/3 left-[8%] w-16 h-16 rounded-xl bg-primary/[0.06] border border-primary/[0.08] rotate-45 blur-[1px]"
      />
      <motion.div
        animate={{ y: [0, 15, 0], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        className="absolute top-[15%] left-[55%] w-6 h-6 rounded-full bg-primary/10 border border-primary/10"
      />
      <motion.div
        animate={{ y: [0, -12, 0], rotate: [0, 180, 360] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-[20%] right-[12%] w-20 h-20 rounded-full border-2 border-primary/[0.06]"
      />
    </div>
  );
}

const blurSlide = {
  hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

export const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const titles = [
    {
      main: "Fidélisez vos clients,",
      accent: "suivez chaque visite,",
      end: "simplement",
    },
    {
      main: "Transformez vos habitués en",
      accent: "communauté engagée",
      end: "qui revient",
    },
    {
      main: "Lancez des campagnes ciblées",
      accent: "qui ramènent vos clients",
      end: "vraiment",
    },
  ];

  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTitleIndex((prev) => (prev + 1) % titles.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [titles.length]);

  return (
    <section
      id="accueil"
      ref={ref}
      className="relative min-h-[auto] lg:min-h-[92vh] flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0 hero-dot-pattern" />
      <FloatingShapes />

      <div className="container relative mx-auto px-4 lg:px-6 py-20 md:py-28 lg:py-32">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-10 xl:gap-14 items-center max-w-7xl mx-auto">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.div variants={blurSlide}>
              <div className="inline-flex items-center gap-2 bg-primary/8 text-primary border border-primary/15 rounded-full px-4 py-1.5 text-sm font-medium mb-8 backdrop-blur-sm">
                <Heart className="h-3.5 w-3.5" />
                Programme de fidélité pour cafés, restaurants et salons
              </div>
            </motion.div>

            <motion.div variants={blurSlide} className="mb-6">
              <div className="h-[140px] sm:h-[120px] lg:h-[140px] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.h1
                    key={currentTitleIndex}
                    initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -40, filter: "blur(8px)" }}
                    transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-foreground leading-[1.1] tracking-tight"
                  >
                    {titles[currentTitleIndex].main}{" "}
                    <span className="text-gradient">
                      {titles[currentTitleIndex].accent}
                    </span>{" "}
                    {titles[currentTitleIndex].end}
                  </motion.h1>
                </AnimatePresence>
              </div>
            </motion.div>

            <motion.p
              variants={blurSlide}
              className="text-base sm:text-lg text-muted-foreground mb-8 sm:mb-10 leading-relaxed max-w-xl"
            >
              Vos clients scannent un QR au comptoir, partagent leur numéro, et
              débloquent aussitôt votre cadeau de bienvenue, leur carte de fidélité
              et vos campagnes SMS. Vous gardez l'historique de chaque visite — et un
              canal direct pour les faire revenir. Conforme à la loi 09-08.
            </motion.p>

            <motion.div
              variants={blurSlide}
              className="flex flex-col sm:flex-row gap-3 mb-10 sm:mb-14"
            >
              <Button
                size="lg"
                className="group rounded-xl px-7 h-12 text-base relative overflow-hidden shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-shadow duration-300"
                asChild
              >
                <a href="#inscription">
                  <span className="relative z-10">Inscrire mon commerce</span>
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform relative z-10" />
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="rounded-xl px-7 h-12 text-base gap-2 backdrop-blur-sm border-border/60 hover:border-primary/30 hover:bg-primary/5 transition-all duration-300"
                asChild
              >
                <a href="#fonctionnement">Voir comment ça marche</a>
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96, filter: "blur(10px)" }}
            animate={
              isInView
                ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
                : {}
            }
            transition={{ duration: 0.9, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="max-w-lg mx-auto lg:max-w-none"
          >
            <StampCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
